






































import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';

import { Chapter, Course, ExperimentGetter, LoggedOutCoupon, Quiz } from 'frontend/types';
import Cover from 'frontend/views/courses/CoursePage/components/Cover.vue';
import ChaptersList from 'frontend/views/courses/CoursePage/components/ChaptersList.vue';
import DetailedDescription from 'frontend/views/courses/CoursePage/components/DetailedDescription.vue';
import OnboardingOverlay from 'frontend/views/courses/CoursePage/components/OnboardingOverlay.vue';
import CourseModal from 'frontend/views/courses/CoursePage/components/CourseModal.vue';

@Component({
  components: {
    ChaptersList,
    CourseModal,
    Cover,
    DetailedDescription,
    OnboardingOverlay,
    LoggedOutCouponBanner: () =>
      // tslint:disable-next-line:space-in-parens max-line-length
      import(
        /* webpackChunkName: 'LoggedOutCouponBanner' */ 'frontend/views/courses/components/LoggedOutCouponBanner.vue'
      ),
  },
  computed: {
    ...mapState('course', ['course']),
    ...mapState('isFirstCourse', ['didNotStartALesson']),
    ...mapGetters('globals', ['isAuthenticated', 'inExperiment']),
    ...mapGetters('chapters', ['chapters']),
    ...mapGetters('showShoppingVersion', ['showShoppingVersion']),
    ...mapGetters('pageHeader', ['isMobile']),
    ...mapGetters('loggedOutCoupon', ['activeLoggedOutCoupon']),
  },
})
export default class CoursePage extends Vue {
  private isAuthenticated!: boolean;
  private showShoppingVersion!: boolean;
  private didNotStartALesson!: boolean;
  private course!: Course;
  private chapters!: Chapter[];
  private activeLoggedOutCoupon!: LoggedOutCoupon;
  private readonly inExperiment!: ExperimentGetter;

  private isOnboardingOverlayOpen: boolean = false;
  private isCourseModalOpen: boolean = false;
  private enableJustCompletedAnimation: boolean = false;

  private get isInEndstateRoutingExperiment(): boolean {
    return !this.inExperiment('endstate_routing_2023_02', 'control');
  }

  private get hasPractice(): boolean {
    return this.chapters.some(chapter =>
      chapter.quizzes.some(lesson => lesson.practice_lessons.length),
    );
  }

  private get previousLessonSlug(): string | undefined {
    return this.$route.query.previousLesson as string | undefined;
  }

  private get computedPreviousLessonSlug(): string | undefined {
    const recommendedLessonIndex = this.allMainLessons.findIndex(
      lesson => lesson.slug === this.course.recommended_lesson_slug,
    );

    if (recommendedLessonIndex > 0) {
      return this.allMainLessons[recommendedLessonIndex - 1].slug;
    }
    return undefined;
  }

  private get shouldHighlightFirstLesson(): boolean {
    return this.isAuthenticated && this.didNotStartALesson;
  }

  private get showLoggedoutCouponBanner(): boolean {
    return !this.isAuthenticated && !!this.activeLoggedOutCoupon;
  }

  private get allMainLessons(): Quiz[] {
    return this.chapters.flatMap(chapter => chapter.quizzes);
  }

  mounted(): void {
    if (this.shouldHighlightFirstLesson) {
      // This compensates some small layout shifts in the page when the page is loaded.
      setTimeout(() => (this.isOnboardingOverlayOpen = true), 500);
    }
  }

  created(): void {
    this.determineIfJustCompletedAnimationShouldBeEnabled();
  }

  private determineIfJustCompletedAnimationShouldBeEnabled(): void {
    if (this.isInEndstateRoutingExperiment && this.previousLessonSlug) {
      const recommendedLessonIndex = this.allMainLessons.findIndex(
        lesson => lesson.slug === this.course.recommended_lesson_slug,
      );
      const previousLessonIndex = this.allMainLessons.findIndex(
        lesson => lesson.slug === this.previousLessonSlug,
      );

      if (
        previousLessonIndex !== -1 &&
        recommendedLessonIndex - 1 === previousLessonIndex &&
        this.allMainLessons[previousLessonIndex].user_state === 'completed'
      ) {
        this.enableJustCompletedAnimation = true;
      }
    }
  }
}
